/**
 * Configuração de ambiente, lida uma vez e validada na carga do módulo.
 *
 * Falhar aqui, no import, é deliberado: um bundle publicado sem `VITE_SUPABASE_URL`
 * produziria telas vazias com erro de rede em cada query — muito mais difícil de
 * diagnosticar do que uma falha imediata e explícita.
 */

function required(name: string, value: string | undefined): string {
  const trimmed = value?.trim()
  if (!trimmed) {
    throw new Error(
      `Variável de ambiente ausente: ${name}. ` +
        'Copie apps/web/.env.example para .env.local e preencha.',
    )
  }
  return trimmed
}

/**
 * Em produção o valor é `/supabase`, servido same-origin pelo nginx do app.
 * Nunca o IP interno do Kong: ele iria parar no bundle, visível a qualquer um
 * que abrir o DevTools.
 */
const supabaseUrl = required('VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL)
const supabaseAnonKey = required('VITE_SUPABASE_ANON_KEY', import.meta.env.VITE_SUPABASE_ANON_KEY)

/** `local` = GoTrue direto (Supabase CLI). `bridge` = spa-bridge same-origin (dev01/prod). */
const authModeRaw = import.meta.env.VITE_AUTH_MODE?.trim() ?? 'bridge'
const authMode = authModeRaw === 'local' ? 'local' : 'bridge'
const devEmail = import.meta.env.VITE_DEV_SUPABASE_EMAIL?.trim()
const devPassword = import.meta.env.VITE_DEV_SUPABASE_PASSWORD?.trim()

// O modo administrativo (login direto no GoTrue, sem SSO) existe para
// DESENVOLVIMENTO. Num build de produção ele pularia o SSO inteiro — e o Vite
// carrega `.env.local` também no `vite build`, então um build feito na máquina
// de quem desenvolve levaria o modo e a senha da conta técnica para o bundle
// público. O `.dockerignore` já tira o arquivo da imagem; esta é a segunda
// trava, e a que não depende de lembrar de arquivo nenhum.
if (authMode === 'local' && !import.meta.env.DEV) {
  throw new Error(
    'VITE_AUTH_MODE=local num build de produção. O modo administrativo é só para ' +
      '`pnpm dev`; em produção o login é sempre pelo SSO.',
  )
}

if (/service_role/.test(supabaseAnonKey)) {
  throw new Error(
    'VITE_SUPABASE_ANON_KEY contém uma service_role key. ' +
      'Essa chave ignora RLS e nunca pode ir para o browser.',
  )
}

export const appEnv = {
  supabaseUrl,
  supabaseAnonKey,

  /** Slug registrado em `portal.applications`. Vai no `?appId=` do login. */
  appId: 'sistemas',

  /**
   * O bridge é sempre same-origin (`/auth/*` e `/api/session` são proxiados
   * pelo nginx em produção e pelo Vite em dev). Base vazia é o padrão atual do
   * ecossistema; a variante com URL absoluta ficou para trás porque quebrava o
   * cookie `portal_sso_bridge_session`, que é HttpOnly e por domínio.
   */
  bridgeBaseUrl: '',

  authMode,
  devAuth:
    authMode === 'local' && devEmail && devPassword
      ? { email: devEmail, password: devPassword }
      : null,

  isDev: import.meta.env.DEV,
} as const
